import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useMutation, useQuery } from "@apollo/client";
import { CURRENT_USER } from "../querys";
import { CREATE_ADDRESS, EDIT_ADDRESS, DELETE_ADDRESS } from "../mutations";
import {
  Select,
  RowFields,
  InputField,
} from "../components/tabs/components/fields";
import { toast } from "react-toastify";

import { customStyles } from "../services/modal";

// Mapeo de países para mostrar vs backend
const COUNTRY_MAPPING = {
  Algeria: "Algeria",
  Andorra: "Andorra",
  Austria: "Austria",
  Belarus: "Belarus",
  Belgium: "Belgium",
  Herzegovina: "Herzegovina",
  Bulgaria: "Bulgaria",
  Croatia: "Croatia",
  Cyprus: "Cyprus",
  Czech: "Czech",
  Denmark: "Denmark",
  Estonia: "Estonia",
  Finland: "Finland",
  France: "France",
  Germany: "Germany",
  Greece: "Greece",
  Hungary: "Hungary",
  Iceland: "Iceland",
  Ireland: "Ireland",
  Italy: "Italy",
  Latvia: "Latvia",
  Lithuania: "Lithuania",
  Luxembourg: "Luxembourg",
  Moldova: "Moldova",
  Netherlands: "Netherlands",
  Norway: "Norway",
  Poland: "Poland",
  Romania: "Romania",
  Russia: "Russia",
  Serbia: "Serbia",
  Slovakia: "Slovakia",
  Slovenia: "Slovenia",
  Spain: "Spain",
  Sweden: "Sweden",
  Swiss: "Swiss",
  Tunisia: "Tunisia",
  Turkey: "Turkey",
  UK: "UK",
  "United States": "United_States", // Mapeo principal
  Ukraine: "Ukraine",
  Montenegro: "Montenegro",
  Liechtenstein: "Liechtenstein",
  Monaco: "Monaco",
  Vatican: "Vatican",
  "San Marino": "San_Marino",
  Kosovo: "Kosovo",
  Indonesia: "Indonesia",
  Japan: "Japan",
  Malaysia: "Malaysia",
  Philippines: "Philippines",
  "Viet Nam": "Viet_Nam",
  Bangladesh: "Bangladesh",
  Cambodia: "Cambodia",
  China: "China",
  India: "India",
  Korea: "Korea",
  Laos: "Laos",
  Tajikistan: "Tajikistan",
  Thailand: "Thailand",
  Singapore: "Singapore",
  Taiwan: "Taiwan",
  Australia: "Australia",
  "New Zealand": "New_Zealand",
};

// Mapeo de estados para mostrar vs backend
const STATE_MAPPING = {
  ALABAMA: "ALABAMA",
  ALASKA: "ALASKA",
  ARIZONA: "ARIZONA",
  ARKANSAS: "ARKANSAS",
  CALIFORNIA: "CALIFORNIA",
  COLORADO: "COLORADO",
  CONNECTICUT: "CONNECTICUT",
  DELAWARE: "DELAWARE",
  FLORIDA: "FLORIDA",
  GEORGIA: "GEORGIA",
  HAWAII: "HAWAII",
  IDAHO: "IDAHO",
  ILLINOIS: "ILLINOIS",
  INDIANA: "INDIANA",
  IOWA: "IOWA",
  KANSAS: "KANSAS",
  KENTUCKY: "KENTUCKY",
  LOUISIANA: "LOUISIANA",
  MAINE: "MAINE",
  MARYLAND: "MARYLAND",
  MASSACHUSETTS: "MASSACHUSETTS",
  MICHIGAN: "MICHIGAN",
  MINNESOTA: "MINNESOTA",
  MISSISSIPPI: "MISSISSIPPI",
  MISSOURI: "MISSOURI",
  MONTANA: "MONTANA",
  NEBRASKA: "NEBRASKA",
  NEVADA: "NEVADA",
  "NEW HAMPSHIRE": "NEW_HAMPSHIRE",
  "NEW JERSEY": "NEW_JERSEY",
  "NEW YORK": "NEW_YORK",
  "NORTH CAROLINA": "NORTH_CAROLINA",
  "NORTH DAKOTA": "NORTH_DAKOTA",
  OHIO: "OHIO",
  OKLAHOMA: "OKLAHOMA",
  OREGON: "OREGON",
  PENNSYLVANIA: "PENNSYLVANIA",
  "RHODE ISLAND": "RHODE_ISLAND",
  "SOUTH CAROLINA": "SOUTH_CAROLINA",
  "SOUTH DAKOTA": "SOUTH_DAKOTA",
  TENNESSEE: "TENNESSEE",
  TEXAS: "TEXAS",
  UTAH: "UTAH",
  VERMONT: "VERMONT",
  VIRGINIA: "VIRGINIA",
  WASHINGTON: "WASHINGTON",
  "WEST VIRGINIA": "WEST_VIRGINIA",
  WISCONSIN: "WISCONSIN",
  WYOMING: "WYOMING",
  "WASHINGTON D.C.": "WASHINGTON_D_C",
};

// Mapeo inverso: del backend al frontend
const REVERSE_COUNTRY_MAPPING = Object.fromEntries(
  Object.entries(COUNTRY_MAPPING).map(([key, value]) => [value, key])
);

const REVERSE_STATE_MAPPING = Object.fromEntries(
  Object.entries(STATE_MAPPING).map(([key, value]) => [value, key])
);

// Arrays para los selects (keys del mapping)
const COUNTRIES = Object.keys(COUNTRY_MAPPING);
const US_STATES = Object.keys(STATE_MAPPING);

// Función para convertir valores del backend al frontend (para mostrar en el form)
const mapValueForFrontend = (fieldName, value) => {
  if (fieldName === "Country" && REVERSE_COUNTRY_MAPPING[value]) {
    return REVERSE_COUNTRY_MAPPING[value];
  }
  if (fieldName === "State" && REVERSE_STATE_MAPPING[value]) {
    return REVERSE_STATE_MAPPING[value];
  }
  return value;
};

const ModalAddress = ({
  positionRow,
  typeModal,
  address,
  setAddress,
  openModal,
  setModalAddress,
  onCancel,
  mainAddress,
}) => {
  Modal.setAppElement("#___gatsby");

  const { data: dataUser } = useQuery(CURRENT_USER);

  // Estado inicial para errores
  const initialErrors = {
    lastname: "",
    firstname: "",
    address: "",
    zipcode: "",
    cityName: "",
    street: "",
    phone: "",
    Country: "",
    State: "",
  };

  const [initialDataNormalized, setInitialDataNormalized] = useState(false);

  const [messageError, setMessageError] = useState(initialErrors);

  // useEffect para normalizar los datos cuando se abre el modal para edición
  useEffect(() => {
    if (
      typeModal === "edit" &&
      address &&
      openModal &&
      !initialDataNormalized
    ) {
      // Normalizar los datos para mostrar correctamente en el form
      const normalizedAddress = {
        ...address,
        Country: mapValueForFrontend("Country", address.Country),
        State: mapValueForFrontend("State", address.State),
      };
      setAddress(normalizedAddress);
      setInitialDataNormalized(true);
    }

    // Resetear el flag cuando se cierra el modal
    if (!openModal) {
      setInitialDataNormalized(false);
    }
  }, [typeModal, openModal, address, initialDataNormalized]);

  // Función para convertir valores del frontend al backend
  const mapValueForBackend = (fieldName, value) => {
    if (fieldName === "Country" && COUNTRY_MAPPING[value]) {
      return COUNTRY_MAPPING[value];
    }
    if (fieldName === "State" && STATE_MAPPING[value]) {
      return STATE_MAPPING[value];
    }
    return value;
  };

  // Validación del formulario
  const formValidate = (errors, addressData) => {
    const hasErrors = Object.values(errors).some((error) => error.length > 0);
    const requiredFields = [
      addressData.lastname,
      addressData.firstname,
      addressData.Country,
      addressData.zipcode,
      addressData.cityName,
      addressData.street,
      addressData.phone,
    ];
    let hasEmptyFields = requiredFields.some((field) => field === "");

    // Si es United States, también validar State
    if (addressData.Country === "United States") {
      hasEmptyFields =
        hasEmptyFields || !addressData.State || addressData.State === "";
    }

    return !hasErrors && !hasEmptyFields;
  };

  // Manejo de cambios en inputs
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validaciones por campo
    const validations = {
      lastname: value === "" ? "This field is required" : "",
      firstname: value === "" ? "This field is required" : "",
      zipcode: value === "" ? "This field is required" : "",
      cityName: value === "" ? "This field is required" : "",
      street: value === "" ? "This field is required" : "",
      phone: value === "" ? "This field is required" : "",
      Country: value === "" ? "This field is required" : "",
      State: value === "" ? "This field is required" : "",
    };

    if (validations[name] !== undefined) {
      setMessageError((prev) => ({ ...prev, [name]: validations[name] }));
    }

    // Limpiar el estado State si se cambia el país y no es United States
    if (name === "Country" && value !== "United States") {
      setAddress((prevState) => ({
        ...prevState,
        [name]: value,
        State: "", // Limpiar state si no es USA
      }));
      // También limpiar error de State
      setMessageError((prev) => ({ ...prev, State: "" }));
    } else {
      // Actualizar el estado del address normalmente
      setAddress((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  // Mutaciones
  const [addAddress, { loading: mutationLoading }] = useMutation(
    CREATE_ADDRESS,
    {
      onCompleted: () => {
        setModalAddress(false);
        toast.success("Your registration was completed successfully");
      },
      onError: (error) => {
        
        toast.error("Please complete the form");
      },
      refetchQueries: ["getAddress", "getMainAddress", "getSecontaryAddress"],
    }
  );

  const [editAddress] = useMutation(EDIT_ADDRESS, {
    onCompleted: () => {
      setModalAddress(false);
      toast.success("Your registration was completed successfully");
    },
    onError: (error) => {
      
      toast.error("Please complete the form");
    },
    refetchQueries: ["getAddress", "getMainAddress", "getSecontaryAddress"],
  });

  const [deleteAddress] = useMutation(DELETE_ADDRESS, {
    onCompleted: () => {
      setModalAddress(false);
      toast.success("successfully removed");
    },
    onError: (error) => {
      toast.error("An error occurred while removing the address");
    },
  });

  // Validar campos requeridos
  const validateRequiredFields = () => {
    const requiredFields = [
      "lastname",
      "firstname",
      "Country",
      "zipcode",
      "cityName",
      "street",
      "phone",
    ];
    const isBasicValid = requiredFields.every(
      (field) => address[field] && address[field] !== ""
    );

    // Si el país es United States, también validar State
    if (address.Country === "United States") {
      return isBasicValid && address.State && address.State !== "";
    }

    return isBasicValid;
  };

  // Manejadores de submit
  const handleSubmit = (e, isEdit = false) => {
    e.preventDefault();

    if (!validateRequiredFields()) {
      toast.error("Please complete the form");
      return;
    }

    if (!formValidate(messageError, address)) {
      toast.error("Please complete the required fields");
      return;
    }

    if (isEdit) {
      handleEditAddress();
    } else {
      handleCreateNewAddress();
    }
  };

  const handleDeleteAddress = () => {
    deleteAddress({
      variables: { id: positionRow },
      refetchQueries: ["getAddress", "getMainAddress", "getSecontaryAddress"],
    });
  };

  const handleCreateNewAddress = () => {
    // Preparar datos base
    const mappedData = {
      Country: mapValueForBackend("Country", address.Country),
      lastname: address.lastname,
      firstname: address.firstname,
      cityName: address.cityName,
      zipcode: address.zipcode,
      aptNumber: address.aptNumber || "",
      phone: address.phone,
      companyName: address.companyName || "",
      street: address.street,
      main: mainAddress === 0,
      users_permissions_user: dataUser.me.id,
    };

    // Solo agregar State si el país es United States Y tiene un valor
    if (address.Country === "United States" && address.State) {
      mappedData.State = mapValueForBackend("State", address.State);
    } else if (address.Country !== "United States") {
      mappedData.State = null; // o "" dependiendo de lo que acepte tu backend
    }
    addAddress({
      variables: { data: mappedData },
    });
  };

  const handleEditAddress = () => {
    // Preparar datos base
    const mappedData = {
      Country: mapValueForBackend("Country", address.Country),
      lastname: address.lastname,
      firstname: address.firstname,
      cityName: address.cityName,
      zipcode: address.zipcode,
      aptNumber: address.aptNumber || "",
      phone: address.phone,
      main: false,
      companyName: address.companyName || "",
      street: address.street,
    };

    if (address.Country === "United States" && address.State) {
      mappedData.State = mapValueForBackend("State", address.State);
    } else if (address.Country !== "United States") {
      mappedData.State = null; 
    }

    editAddress({
      variables: {
        id: positionRow,
        data: mappedData,
      },
    });
  };

  return (
    <Modal isOpen={openModal} style={customStyles}>
      <form className="container address-modal" noValidate>
        {/* Full Name */}
        <RowFields className="row u-width-100">
          <div className="col-lg-2 col-md-3">
            <label htmlFor="firstname">Full Name</label>
          </div>
          <div className="col-lg-5 col-md-5 col-sm-6 u-margin-top-1-5-rp-575">
            <InputField>
              <input
                type="text"
                placeholder="First Name (required)"
                name="firstname"
                onChange={handleChange}
                className={messageError.firstname ? "border-error" : ""}
                value={address?.firstname || ""}
              />
              {messageError.firstname && (
                <small className="field-error field-error--address-form">
                  {messageError.firstname}
                </small>
              )}
            </InputField>
          </div>
          <div className="col-lg-5 col-md-4 col-sm-6">
            <InputField>
              <input
                type="text"
                placeholder="Last Name (required)"
                name="lastname"
                onChange={handleChange}
                className={messageError.lastname ? "border-error" : ""}
                value={address?.lastname || ""}
              />
              {messageError.lastname && (
                <small className="field-error field-error--address-form">
                  {messageError.lastname}
                </small>
              )}
            </InputField>
          </div>
        </RowFields>

        {/* Company Name */}
        <RowFields className="row u-width-100">
          <div className="col-lg-2 col-md-3">
            <label htmlFor="companyName">Company name</label>
          </div>
          <div className="col-lg-10 col-md-9">
            <InputField>
              <input
                type="text"
                placeholder="Company Name (optional)"
                name="companyName"
                onChange={handleChange}
                value={address?.companyName || ""}
              />
            </InputField>
          </div>
        </RowFields>

        {/* Address Section */}
        <RowFields className="row u-width-100">
          <div className="col-lg-2 col-md-3">
            <label htmlFor="street">Address</label>
          </div>
          <div className="col-lg-10 col-md-9 col-sm-12 u-margin-top-1-5">
            <InputField>
              <input
                type="text"
                placeholder="Street · building name (eg 1-3-5) (required)"
                name="street"
                onChange={handleChange}
                className={messageError.street ? "border-error" : ""}
                value={address?.street || ""}
              />
              {messageError.street && (
                <small className="field-error field-error--address-form address-error">
                  {messageError.street}
                </small>
              )}
            </InputField>
          </div>

          <div className="col-lg-2 col-md-3 order-lg-0"></div>
          <div className="col-lg-5 col-md-4 col-sm-6 u-margin-top-1-5">
            <InputField>
              <input
                type="text"
                placeholder="Apt/Unit Number"
                name="aptNumber"
                onChange={handleChange}
                value={address?.aptNumber || ""}
              />
            </InputField>
          </div>
          <div className="col-lg-5 col-md-5 col-sm-6 u-margin-top-1-5">
            <InputField>
              <input
                type="text"
                placeholder="City name (example: Kanda Jimbocho, Chiyoda ku) (required)"
                name="cityName"
                onChange={handleChange}
                className={messageError.cityName ? "border-error" : ""}
                value={address?.cityName || ""}
              />
              {messageError.cityName && (
                <small className="field-error field-error--address-form">
                  {messageError.cityName}
                </small>
              )}
            </InputField>
          </div>

          <div className="col-lg-2 col-md-3"></div>
          <div className="col-lg-9 col-md-9 col-sm-9 u-margin-top-1-5-rp-575">
            <p className="not-pobox">*NOT available to use PO Box.</p>
          </div>

          {/* Country and State Selection */}
          <div className="p-0" style={{ display: "flex", flexWrap: "wrap" }}>
            <div className="col-lg-2 col-md-3 order-0 pl-12 pr-12 prl-0"></div>

            {/* Country Select */}
            <div className="col-lg-5 col-md-4 col-sm-9 u-margin-top-1-5-rp-575 order-1 pl-12 pr-12 w-100-mob">
              <InputField>
                <Select
                  value={address?.Country || ""}
                  name="Country"
                  onChange={handleChange}
                  className={messageError.Country ? "border-error" : ""}
                >
                  <option value="" disabled>
                    Select Country
                  </option>
                  {COUNTRIES.map((countryOption, i) => (
                    <option key={i} value={countryOption}>
                      {countryOption}
                    </option>
                  ))}
                </Select>
                {messageError.Country && (
                  <small className="field-error field-error--address-form">
                    {messageError.Country}
                  </small>
                )}
              </InputField>
            </div>

            {/* Zip Code */}
            <div className="col-lg-5 col-md-5 col-sm-3 order-2 pl-12 pr-12 w-100-mob order-2-address">
              <InputField>
                <input
                  type="number"
                  placeholder="Zip code (required)"
                  name="zipcode"
                  onChange={handleChange}
                  className={messageError.zipcode ? "border-error" : ""}
                  value={address?.zipcode || ""}
                />
                {messageError.zipcode && (
                  <small className="field-error field-error--address-form">
                    {messageError.zipcode}
                  </small>
                )}
              </InputField>
            </div>

            {/* US States (solo si se selecciona United States) */}
            {address?.Country === "United States" && (
              <>
                <div className="col-lg-2 col-md-3 order-3 pl-12 pr-12 prl-0"></div>
                <div className="col-lg-5 col-md-4 col-sm-9 u-margin-top-1-5 order-4 pl-12 pr-12 w-100-mob order-1-address">
                  <InputField>
                    <Select
                      value={address?.State || ""}
                      name="State"
                      onChange={handleChange}
                      className={messageError.State ? "border-error" : ""}
                    >
                      <option value="" disabled>
                        Select State
                      </option>
                      {US_STATES.map((stateOption, i) => (
                        <option key={i} value={stateOption}>
                          {stateOption}
                        </option>
                      ))}
                    </Select>
                    {messageError.State && (
                      <small className="field-error field-error--address-form">
                        {messageError.State}
                      </small>
                    )}
                  </InputField>
                </div>
              </>
            )}
          </div>
        </RowFields>

        {/* Phone */}
        <RowFields className="row u-width-100">
          <div className="col-lg-2 col-md-3 col-sm-2 u-margin-top-1-5-rp">
            <label htmlFor="phone">Phone</label>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-10 u-margin-top-1-5-rp u-margin-top-1-5-rp-575">
            <InputField>
              <input
                type="tel"
                placeholder="Phone Number (required)"
                name="phone"
                onChange={handleChange}
                className={messageError.phone ? "border-error" : ""}
                value={address?.phone || ""}
              />
              {messageError.phone && (
                <small className="field-error field-error--address-form">
                  {messageError.phone}
                </small>
              )}
            </InputField>
          </div>
        </RowFields>

        {/* Buttons */}
        <div className="u-text-center buttons-modal">
          <input
            type="button"
            onClick={() => {
              onCancel();
              setMessageError(initialErrors);
            }}
            className="btn btn-primary u-size-150"
            defaultValue="Cancel"
          />

          {typeModal === "add" ? (
            <button
              type="submit"
              className="btn btn-danger u-size-150"
              onClick={(e) => handleSubmit(e, false)}
            >
              {mutationLoading ? <div className="loading"></div> : "Add"}
            </button>
          ) : (
            <>
              <button
                type="submit"
                className="btn btn-danger u-size-150"
                onClick={(e) => handleSubmit(e, true)}
              >
                {mutationLoading ? (
                  <div className="loading"></div>
                ) : (
                  "Save Changes"
                )}
              </button>
              <hr />
              <button
                className="btn btn-secondary u-size-150 u-margin-left"
                type="button"
                onClick={handleDeleteAddress}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ModalAddress;
